import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { MyProfileService } from '../profile/my-profile.service';
import { APP_PATHS } from '../routing/app-paths';
import { CUSTOMER_TYPE } from './customer-type';

/**
 * Establishment-only routes (Q-63/A) — today exactly one: `panel/zapotrzebowanie`.
 *
 * The account type is NOT in the front's memory: login and refresh answer only
 * `{ csrfToken }` (`CustomerSessionService`), so the one source is `GET /my/profile`
 * via `MyProfileService` — cached, so this guard, the sidenav and the screen share a
 * single request.
 *
 * The comparison is equality with the constant, never truthiness or `!==
 * individual`: `CUSTOMER_TYPE.individual` is `0`, and a value outside `{0, 1}` must
 * count as "no access", not as an establishment. FAIL-CLOSED: a failed or malformed
 * profile denies. The denial is silent by design — the redirect lands on the panel's
 * default section ("Moje szkolenia"); the loud 403 belongs to the backend, which
 * this customer never reaches.
 *
 * 401 is deliberately not handled here: an invalid session is `customerAuthGuard`'s
 * job on the `/panel` parent (it runs first) and the interceptor's silent refresh.
 */
export const establishmentOnlyGuard: CanActivateFn = async () => {
  const profile = inject(MyProfileService);
  const router = inject(Router);

  try {
    const { customerType } = await firstValueFrom(profile.load());

    if (customerType === CUSTOMER_TYPE.establishment) {
      return true;
    }
  } catch {
    // Fail-closed: an unavailable profile is "no access", never "let them in".
    // The failure class does not matter to a route decision, so none is read.
  }

  return router.createUrlTree(['/', APP_PATHS.panel]);
};
